// =====================================================
// MULTI-FORMAT PARSER — wrapped as ES module from src/js/parsers.js
// PDF parser extracted to pro/pdf-parser.ts
// Logic unchanged.
// =====================================================
// @ts-nocheck — preserving vanilla JS patterns during migration

import type { RichParagraph, ParsedDocument, RichToken, CellData, CellInfo, InlineEvent } from '../types';
import { W_NS, serializeNode, serializeClean, parseEl } from './xml-helpers';
import { buildDocLayout } from './doc-layout';
import { getPdfParser, isProActive } from '../bridge';
import JSZip from 'jszip';
import { UNREADABLE_DOCX, PASSWORD_PROTECTED, isOleFile, isEncryptedOoxml, openDocxZip } from './docx-package';
export { UNREADABLE_DOCX, PASSWORD_PROTECTED } from './docx-package';

function getFileExtension(filename: string): string {
  return (filename || '').toLowerCase().split('.').pop() || '';
}

const FREE_PAGE_LIMIT = 5;
// ~3000 characters per page (standard US letter, 12pt, normal margins)
const CHARS_PER_PAGE = 3000;

type FileKind = 'zip' | 'pdf' | 'rtf' | 'ole' | 'unknown';

/** What a file is by its leading bytes, whatever its name says. */
async function sniffFileKind(file: File, ext: string): Promise<FileKind> {
  let head: Uint8Array;
  try { head = new Uint8Array(await file.slice(0, 1024).arrayBuffer()); } catch { return 'unknown'; }
  if (head.length >= 4 && head[0] === 0x50 && head[1] === 0x4B &&
      ((head[2] === 3 && head[3] === 4) || (head[2] === 5 && head[3] === 6) || (head[2] === 7 && head[3] === 8))) return 'zip';
  if (isOleFile(head)) return 'ole';
  let ascii = '';
  for (let i = 0; i < head.length; i++) ascii += String.fromCharCode(head[i]);
  const lead = ascii.replace(/^\xEF\xBB\xBF/, '').replace(/^\s+/, '');
  if (lead.startsWith('{\\rtf')) return 'rtf';
  if (lead.startsWith('%PDF-')) return 'pdf';
  // the PDF format allows a header anywhere in the first 1024 bytes; a text file that merely
  // mentions "%PDF-" stays text
  if (ext !== 'txt' && ext !== 'md' && ascii.includes('%PDF-')) return 'pdf';
  return 'unknown';
}

export async function parseDocument(file: File): Promise<ParsedDocument> {
  const named = getFileExtension(file.name);
  // Parse by content when the name and the bytes disagree: a PDF saved as .docx, a .docx sent
  // as .doc or .pdf, an RTF saved as .doc. A file the name calls something else and the bytes
  // do not identify is parsed by its name (and fails with that format's message).
  const kind = await sniffFileKind(file, named);
  let ext = named;
  if (kind === 'zip') ext = 'docx';
  else if (kind === 'pdf') ext = 'pdf';
  else if (kind === 'rtf') ext = 'rtf';
  else if (kind === 'ole') {
    if (isEncryptedOoxml(new Uint8Array(await file.arrayBuffer()))) throw new Error(PASSWORD_PROTECTED);
    ext = 'doc';
  }
  let result: ParsedDocument;
  switch (ext) {
    case 'docx':
      result = await parseDocxFile(file);
      break;
    case 'doc':
      result = await parseDocFile(file);
      break;
    case 'pdf': {
      const pdfParser = getPdfParser();
      if (!pdfParser) {
        throw new Error('Comparing PDFs needs the full version of Fredline.');
      }
      try {
        result = await pdfParser(file);
      } catch (e: any) {
        // pdf.js's own words ("Invalid PDF structure.", "No password given") are not for users
        const name = e?.name || '', msg = String(e?.message || e);
        if (name === 'PasswordException' || /password/i.test(msg) && !/^OCR/.test(msg)) throw new Error('This PDF is password-protected. Remove the password, save it, and try again.');
        if (name === 'InvalidPDFException' || /Invalid PDF structure|Missing PDF|PDF header/i.test(msg)) throw new Error("This file isn't a readable PDF. It may be damaged, or it may not really be a .pdf.");
        throw e;
      }
      break;
    }
    case 'txt':
    case 'md':
      result = await parseTextFile(file);
      break;
    case 'rtf':
      result = await parseRtfFile(file);
      break;
    default:
      throw new Error(`Unsupported file type: .${ext}`);
  }

  // Free tier: mark cutoff at page limit (compare first 5 pages, rest shown unchanged)
  // Use character count for page estimation — much more reliable than paragraph count
  const proActive = isProActive();
  let totalChars = 0;
  for (const p of result.richParagraphs) totalChars += p.text.length;
  const estPages = Math.max(1, Math.ceil(totalChars / CHARS_PER_PAGE));
  if (!proActive) {
    if (estPages > FREE_PAGE_LIMIT) {
      const maxChars = FREE_PAGE_LIMIT * CHARS_PER_PAGE;
      let charsSoFar = 0;
      let cutIdx = result.richParagraphs.length;
      for (let i = 0; i < result.richParagraphs.length; i++) {
        charsSoFar += result.richParagraphs[i].text.length;
        if (charsSoFar >= maxChars) {
          cutIdx = i + 1;
          break;
        }
      }
      // Don't truncate — keep all paragraphs but record where the free tier comparison stops
      result.truncated = true;
      result.totalEstPages = estPages;
      result.freePageCutoffIdx = cutIdx;
    }
  }

  return result;
}

// Build a plain-text rich paragraph (no formatting, used by non-docx parsers)
function buildPlainParagraph(text: string): RichParagraph {
  const tokens = tokenizeToRich(text, '');
  return {
    text: text,
    pPrXml: '',
    richTokens: tokens,
    rawXml: null,
    inTable: false,
  };
}

// Build plain paragraph array from raw text, splitting on newlines
export function textToParagraphs(content: string): RichParagraph[] {
  const lines = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const paragraphs: RichParagraph[] = [];
  for (const line of lines) {
    paragraphs.push(buildPlainParagraph(line));
  }
  return paragraphs;
}

// ---- .txt / .md parser ----
async function parseTextFile(file: File): Promise<ParsedDocument> {
  // UTF-8 unless a byte-order mark says UTF-16 (Windows Notepad's "Unicode" saves that way;
  // read as UTF-8 it would be every other character a NUL)
  const bytes = new Uint8Array(await file.arrayBuffer());
  const enc = bytes[0] === 0xFF && bytes[1] === 0xFE ? 'utf-16le' : bytes[0] === 0xFE && bytes[1] === 0xFF ? 'utf-16be' : 'utf-8';
  const text = new TextDecoder(enc).decode(bytes);
  return {
    zip: null,
    xmlDoc: null,
    docXml: null,
    richParagraphs: textToParagraphs(text),
    isPlainText: true,
  };
}

// ---- .rtf parser (basic text extraction) ----
async function parseRtfFile(file: File): Promise<ParsedDocument> {
  const raw = await file.text();
  const text = extractRtfText(raw);
  return {
    zip: null,
    xmlDoc: null,
    docXml: null,
    richParagraphs: textToParagraphs(text),
    isPlainText: true,
  };
}

function extractRtfText(rtf: string): string {
  let result = '';
  let depth = 0;
  let i = 0;
  const skipGroups = ['fonttbl', 'colortbl', 'stylesheet', 'info', 'pict', 'header', 'footer', 'footnote', 'field'];

  while (i < rtf.length) {
    const ch = rtf[i];
    if (ch === '{') {
      depth++;
      const ahead = rtf.substring(i + 1, i + 20);
      if (skipGroups.some(sg => ahead.match(new RegExp('^\\\\\\*?\\\\?' + sg + '\\b')))) {
        let nest = 1;
        i++;
        while (i < rtf.length && nest > 0) {
          if (rtf[i] === '{') nest++;
          else if (rtf[i] === '}') nest--;
          i++;
        }
        depth--;
        continue;
      }
      i++;
      continue;
    }
    if (ch === '}') {
      depth--;
      i++;
      continue;
    }
    if (ch === '\\') {
      i++;
      if (i >= rtf.length) break;
      const next = rtf[i];
      if (next === '\n' || next === '\r') { i++; continue; }
      if (next === '{' || next === '}' || next === '\\') {
        result += next; i++; continue;
      }
      let word = '';
      while (i < rtf.length && /[a-zA-Z]/.test(rtf[i])) { word += rtf[i]; i++; }
      let param = '';
      if (i < rtf.length && (rtf[i] === '-' || /\d/.test(rtf[i]))) {
        if (rtf[i] === '-') { param += '-'; i++; }
        while (i < rtf.length && /\d/.test(rtf[i])) { param += rtf[i]; i++; }
      }
      if (i < rtf.length && rtf[i] === ' ') i++;
      if (word === 'par' || word === 'line') result += '\n';
      else if (word === 'tab') result += '\t';
      else if (word === 'lquote' || word === 'rquote') result += "'";
      else if (word === 'ldblquote' || word === 'rdblquote') result += '"';
      else if (word === 'endash') result += '\u2013';
      else if (word === 'emdash') result += '\u2014';
      else if (word === 'bullet') result += '\u2022';
      else if (word === 'u' && param) {
        const code = parseInt(param);
        if (code > 0) result += String.fromCharCode(code);
      }
      continue;
    }
    result += ch;
    i++;
  }
  return result;
}

// ---- OLE2/CFBF parser + Word piece-table text extractor ----
const CP1252_MAP: Record<number, number> = {
  0x80:0x20AC, 0x82:0x201A, 0x83:0x0192, 0x84:0x201E, 0x85:0x2026, 0x86:0x2020,
  0x87:0x2021, 0x88:0x02C6, 0x89:0x2030, 0x8A:0x0160, 0x8B:0x2039, 0x8C:0x0152,
  0x8E:0x017D, 0x91:0x2018, 0x92:0x2019, 0x93:0x201C, 0x94:0x201D, 0x95:0x2022,
  0x96:0x2013, 0x97:0x2014, 0x98:0x02DC, 0x99:0x2122, 0x9A:0x0161, 0x9B:0x203A,
  0x9C:0x0153, 0x9E:0x017E, 0x9F:0x0178,
};

function extractDocText(arrayBuffer: ArrayBuffer): string {
  const data = new DataView(arrayBuffer);
  const bytes = new Uint8Array(arrayBuffer);
  const fileLen = arrayBuffer.byteLength;

  if (fileLen < 512) throw new Error('File too small for OLE2');
  const sig0 = data.getUint32(0, true);
  const sig1 = data.getUint32(4, true);
  if (sig0 !== 0xE011CFD0 || sig1 !== 0xE11AB1A1) throw new Error('Not an OLE2 file');

  const sectorSizeShift = data.getUint16(0x1E, true);
  const sectorSize = 1 << sectorSizeShift;
  const miniSectorSizeShift = data.getUint16(0x20, true);
  const miniSectorSize = 1 << miniSectorSizeShift;
  const firstDirSectorId = data.getInt32(0x30, true);
  const miniStreamCutoff = data.getUint32(0x38, true);
  const firstMiniSATSectorId = data.getInt32(0x3C, true);
  const numMiniSATSectors = data.getUint32(0x40, true);
  const firstDIFATSectorId = data.getInt32(0x44, true);
  const numDIFATSectors = data.getUint32(0x48, true);

  function secOffset(secId: number) { return (secId + 1) * sectorSize; }
  function readI32(off: number) { return off + 4 <= fileLen ? data.getInt32(off, true) : -1; }

  // Build the FAT
  const msatEntries: number[] = [];
  for (let i = 0; i < 109; i++) {
    const secId = readI32(0x4C + i * 4);
    if (secId < 0) break;
    msatEntries.push(secId);
  }
  let difatSec = firstDIFATSectorId;
  for (let d = 0; d < numDIFATSectors && difatSec >= 0; d++) {
    const off = secOffset(difatSec);
    const entriesPerSec = (sectorSize / 4) - 1;
    for (let i = 0; i < entriesPerSec; i++) {
      const secId = readI32(off + i * 4);
      if (secId < 0) break;
      msatEntries.push(secId);
    }
    difatSec = readI32(off + entriesPerSec * 4);
  }

  const entriesPerFATSector = sectorSize / 4;
  const fat = new Int32Array(msatEntries.length * entriesPerFATSector);
  for (let i = 0; i < msatEntries.length; i++) {
    const off = secOffset(msatEntries[i]);
    for (let j = 0; j < entriesPerFATSector; j++) {
      fat[i * entriesPerFATSector + j] = readI32(off + j * 4);
    }
  }

  function readSectorChain(startSec: number, maxLen?: number): Uint8Array {
    const chunks: Uint8Array[] = [];
    let sec = startSec;
    let total = 0;
    while (sec >= 0 && (maxLen === undefined || total < maxLen)) {
      const off = secOffset(sec);
      const remain = maxLen !== undefined ? Math.min(sectorSize, maxLen - total) : sectorSize;
      if (off + remain <= fileLen) {
        chunks.push(new Uint8Array(arrayBuffer, off, remain));
      }
      total += remain;
      sec = (sec < fat.length) ? fat[sec] : -2;
    }
    const result = new Uint8Array(total > (maxLen || total) ? maxLen! : total);
    let pos = 0;
    for (const chunk of chunks) {
      result.set(chunk, pos);
      pos += chunk.length;
    }
    return result;
  }

  // Read directory entries
  const dirData = readSectorChain(firstDirSectorId);
  const dirEntrySize = 128;
  const numDirEntries = Math.floor(dirData.length / dirEntrySize);
  const dirView = new DataView(dirData.buffer, dirData.byteOffset, dirData.byteLength);

  const streams: Record<string, { start: number; size: number }> = {};
  let rootStart = -1, rootSize = 0;

  for (let i = 0; i < numDirEntries; i++) {
    const entryOff = i * dirEntrySize;
    const nameLen = dirView.getUint16(entryOff + 0x40, true);
    let name = '';
    for (let c = 0; c < (nameLen / 2) - 1; c++) {
      name += String.fromCharCode(dirView.getUint16(entryOff + c * 2, true));
    }
    const objType = dirData[entryOff + 0x42];
    const startSec = dirView.getInt32(entryOff + 0x74, true);
    const size = dirView.getUint32(entryOff + 0x78, true);

    if (objType === 5) { rootStart = startSec; rootSize = size; }
    if (objType === 2 && name) { streams[name] = { start: startSec, size: size }; }
  }

  // Read mini-FAT and mini-stream
  let miniFAT = new Int32Array(0);
  let miniStreamData = new Uint8Array(0);

  if (firstMiniSATSectorId >= 0 && rootStart >= 0) {
    const miniFATData = readSectorChain(firstMiniSATSectorId);
    const miniFATView = new DataView(miniFATData.buffer, miniFATData.byteOffset, miniFATData.byteLength);
    miniFAT = new Int32Array(miniFATData.length / 4);
    for (let i = 0; i < miniFAT.length; i++) {
      miniFAT[i] = miniFATView.getInt32(i * 4, true);
    }
    miniStreamData = readSectorChain(rootStart, rootSize);
  }

  function readMiniSectorChain(startSec: number, streamSize: number): Uint8Array {
    const chunks: Uint8Array[] = [];
    let sec = startSec;
    let total = 0;
    while (sec >= 0 && total < streamSize) {
      const off = sec * miniSectorSize;
      const remain = Math.min(miniSectorSize, streamSize - total);
      if (off + remain <= miniStreamData.length) {
        chunks.push(miniStreamData.slice(off, off + remain));
      }
      total += remain;
      sec = (sec < miniFAT.length) ? miniFAT[sec] : -2;
    }
    const result = new Uint8Array(total > streamSize ? streamSize : total);
    let pos = 0;
    for (const chunk of chunks) {
      result.set(chunk, pos);
      pos += chunk.length;
    }
    return result;
  }

  function readStream(name: string): Uint8Array | null {
    const entry = streams[name];
    if (!entry) return null;
    if (entry.size < miniStreamCutoff && entry.size > 0) {
      return readMiniSectorChain(entry.start, entry.size);
    }
    return readSectorChain(entry.start, entry.size);
  }

  // Read the WordDocument stream and parse the FIB
  const wordDocStream = readStream('WordDocument');
  if (!wordDocStream || wordDocStream.length < 0x01AA) {
    throw new Error('WordDocument stream not found or too small');
  }

  const wdView = new DataView(wordDocStream.buffer, wordDocStream.byteOffset, wordDocStream.byteLength);

  const wordMagic = wdView.getUint16(0, true);
  if (wordMagic !== 0xA5EC && wordMagic !== 0xA5DC) {
    throw new Error('Invalid Word document magic number');
  }

  const fibFlags = wdView.getUint16(0x0A, true);
  const tableName = (fibFlags & 0x0200) ? '1Table' : '0Table';

  const fcClx = wdView.getUint32(0x01A2, true);
  const lcbClx = wdView.getUint32(0x01A6, true);

  if (lcbClx === 0) throw new Error('No piece table (lcbClx=0)');

  const tableStream = readStream(tableName);
  if (!tableStream || tableStream.length < fcClx + lcbClx) {
    throw new Error(`Table stream '${tableName}' not found or too small`);
  }

  const tblView = new DataView(tableStream.buffer, tableStream.byteOffset, tableStream.byteLength);

  let clxPos = fcClx;
  const clxEnd = fcClx + lcbClx;
  let pieceTableOffset = -1;
  let pieceTableLen = 0;

  while (clxPos < clxEnd) {
    const clxType = tableStream[clxPos];
    if (clxType === 0x02) {
      pieceTableLen = tblView.getUint32(clxPos + 1, true);
      pieceTableOffset = clxPos + 5;
      break;
    } else if (clxType === 0x01) {
      const grpLen = tblView.getUint16(clxPos + 1, true);
      clxPos += 3 + grpLen;
    } else {
      clxPos++;
    }
  }

  if (pieceTableOffset < 0) throw new Error('Piece table not found in CLX');

  const numPieces = Math.floor((pieceTableLen - 4) / 12);
  if (numPieces <= 0) throw new Error('No pieces in piece table');

  const cpStart = pieceTableOffset;
  const pcdStart = cpStart + (numPieces + 1) * 4;

  const textParts: string[] = [];

  for (let i = 0; i < numPieces; i++) {
    const cpBegin = tblView.getUint32(cpStart + i * 4, true);
    const cpEnd = tblView.getUint32(cpStart + (i + 1) * 4, true);
    const numChars = cpEnd - cpBegin;
    if (numChars <= 0) continue;

    const pcdOff = pcdStart + i * 8;
    let fc = tblView.getUint32(pcdOff + 2, true);

    const isCompressed = (fc & 0x40000000) !== 0;
    fc = fc & ~0x40000000;

    if (isCompressed) {
      const byteOffset = Math.floor(fc / 2);
      let piece = '';
      for (let c = 0; c < numChars; c++) {
        const off = byteOffset + c;
        if (off >= wordDocStream.length) break;
        const b = wordDocStream[off];
        if (b >= 0x80 && b <= 0x9F && CP1252_MAP[b]) {
          piece += String.fromCharCode(CP1252_MAP[b]);
        } else {
          piece += String.fromCharCode(b);
        }
      }
      textParts.push(piece);
    } else {
      let piece = '';
      for (let c = 0; c < numChars; c++) {
        const off = fc + c * 2;
        if (off + 1 >= wordDocStream.length) break;
        piece += String.fromCharCode(wdView.getUint16(off, true));
      }
      textParts.push(piece);
    }
  }

  let text = textParts.join('');
  text = text
    .replace(/\r\n?/g, '\n')
    .replace(/\x07/g, '\t')
    .replace(/[\x00-\x06\x08\x0E-\x1F]/g, '')
    .replace(/\x0C/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return text;
}

// ---- .doc parser ----
async function parseDocFile(file: File): Promise<ParsedDocument> {
  const arrayBuf = await file.arrayBuffer();

  // Some .doc files are actually .docx (wrong extension). Try ZIP first.
  try {
    const zip = await JSZip.loadAsync(arrayBuf);
    const docFile = zip.file('word/document.xml');
    if (docFile) {
      const docXml = await docFile.async('string');
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(docXml, 'application/xml');
      const parseError = xmlDoc.querySelector('parsererror');
      if (!parseError) {
        return { zip, xmlDoc, docXml, richParagraphs: extractRichParagraphs(xmlDoc) };
      }
    }
  } catch(e) { /* Not a ZIP — try OLE2 extraction */ }

  // Try proper OLE2 piece-table text extraction
  try {
    const text = extractDocText(arrayBuf);
    if (text.length >= 20) {
      return {
        zip: null, xmlDoc: null, docXml: null,
        richParagraphs: textToParagraphs(text),
        isPlainText: true,
      };
    }
  } catch(e) { /* OLE2 extraction failed — fall back to brute force */ }

  // Last resort: brute-force byte scan
  try {
    const bytes = new Uint8Array(arrayBuf);
    let text = '';
    for (let i = 0; i < bytes.length; i++) {
      const b = bytes[i];
      if (b === 13) { text += '\n'; }
      else if (b === 10 && (i === 0 || bytes[i-1] !== 13)) { text += '\n'; }
      else if (b === 9) { text += '\t'; }
      else if (b >= 32 && b < 127) { text += String.fromCharCode(b); }
      else if (b >= 160 && b <= 255) { text += String.fromCharCode(b); }
    }
    text = text.replace(/\n{3,}/g, '\n\n').trim();
    if (text.length < 20) {
      throw new Error('Could not extract meaningful text from this .doc file. Try saving it as .docx in Word first.');
    }
    return {
      zip: null, xmlDoc: null, docXml: null,
      richParagraphs: textToParagraphs(text),
      isPlainText: true,
    };
  } catch(e: any) {
    if (e.message.includes('Could not extract')) throw e;
    throw new Error('Unable to read this .doc file. Please save it as .docx in Word and try again.');
  }
}

// ---- .docx parser ----
async function parseDocxFile(file: File): Promise<ParsedDocument> {
  const arrayBuf = await file.arrayBuffer();
  // A damaged or truncated zip, a zip that is not a Word document (.xlsx, .pptx, .odt), or a
  // body that is not XML: one plain message, never JSZip's text (which names its website).
  let zip: any, docXml: string, xmlDoc: Document;
  zip = await openDocxZip(arrayBuf);   // (throws the plain messages)
  try {
    docXml = await zip.file('word/document.xml').async('string');
    // (xmldom throws on XML that is not well-formed, and would also print it to the console
    // unless given its own error handler; a browser's DOMParser returns <parsererror>)
    xmlDoc = new DOMParser({ onError: () => {} }).parseFromString(docXml, 'application/xml');
    if (xmlDoc.querySelector('parsererror') || !xmlDoc.documentElement) throw new Error('document.xml is not XML');
  } catch (e) {
    throw new Error(UNREADABLE_DOCX);
  }
  // Strict Open XML (File > Save As > Strict Open XML Document) uses other namespaces: read as
  // a transitional document it would look empty and compare as "everything deleted".
  if (xmlDoc.getElementsByTagNameNS(W_NS, 'body').length === 0) {
    if (/purl\.oclc\.org\/ooxml\/wordprocessingml\/main/.test(docXml.slice(0, 4000))) {
      throw new Error('This document is saved as Strict Open XML, which Fredline cannot read. Save it in Word as a Word Document (.docx) and try again.');
    }
    throw new Error(UNREADABLE_DOCX);
  }

  const richParagraphs = extractRichParagraphs(xmlDoc);

  // Extract default font: theme minorFont > docDefaults > Normal style
  let defaultFont: string | undefined;
  let defaultFontSize: number | undefined;

  // 1. Check theme for body font (most modern docs use this)
  const themeFile = zip.file('word/theme/theme1.xml');
  if (themeFile) {
    const themeXml = await themeFile.async('string');
    const minorMatch = themeXml.match(/<a:minorFont>([\s\S]*?)<\/a:minorFont>/);
    if (minorMatch) {
      const latin = minorMatch[1].match(/a:latin typeface="([^"]+)"/);
      if (latin) defaultFont = latin[1];
    }
  }

  // 2. Check styles.xml for font size and fallback font
  const stylesFile = zip.file('word/styles.xml');
  if (stylesFile) {
    const stylesXml = await stylesFile.async('string');
    const ddMatch = stylesXml.match(/<w:docDefaults>([\s\S]*?)<\/w:docDefaults>/);
    if (ddMatch) {
      const szMatch = ddMatch[1].match(/w:sz w:val="(\d+)"/);
      if (szMatch) defaultFontSize = Number(szMatch[1]) / 2;
      // Only use docDefaults font if no theme font was found
      if (!defaultFont) {
        const fontMatch = ddMatch[1].match(/w:ascii="([^"]+)"/);
        if (fontMatch) defaultFont = fontMatch[1];
      }
    }
    // 3. Fall back to Normal style
    if (!defaultFont) {
      const normalMatch = stylesXml.match(/<w:style w:type="paragraph" w:default="1"[^>]*>([\s\S]*?)<\/w:style>/);
      if (normalMatch) {
        const fontMatch = normalMatch[1].match(/w:ascii="([^"]+)"/);
        if (fontMatch) defaultFont = fontMatch[1];
        if (!defaultFontSize) {
          const szMatch = normalMatch[1].match(/w:sz w:val="(\d+)"/);
          if (szMatch) defaultFontSize = Number(szMatch[1]) / 2;
        }
      }
    }
  }

  // Preview-only formatting (styles, numbering, page). Attached to the paragraphs by a
  // WeakMap; nothing the DOCX generator reads changes.
  try { await buildDocLayout(zip, docXml, richParagraphs); } catch (e) { console.warn('Fredline: preview layout unavailable', e); }

  return { zip, xmlDoc, docXml, richParagraphs, defaultFont, defaultFontSize };
}

export function extractRichParagraphs(xmlDoc: Document): RichParagraph[] {
  const paragraphs: RichParagraph[] = [];
  const body = xmlDoc.getElementsByTagNameNS(W_NS, 'body')[0];
  if (!body) return paragraphs;

  const bodyKids = Array.from(body.childNodes).filter(n => n.nodeType === 1) as Element[];
  for (let i = 0; i < bodyKids.length; i++) {
    const child = bodyKids[i];
    const localName = child.localName;

    if (localName === 'p') {
      // a paragraph the draft joined to the next one (its mark deleted, tracked) is read joined
      const run = joinRun(bodyKids, i);
      paragraphs.push(parseOneParagraph(run.el));
      i = run.last;
    } else if (localName === 'tbl') {
      let tablePropsXml = '';
      for (let ti = 0; ti < child.childNodes.length; ti++) {
        const tc = child.childNodes[ti] as Element;
        if (tc.nodeType !== 1) continue;
        if (tc.localName === 'tblPr' || tc.localName === 'tblGrid') {
          tablePropsXml += serializeNode(tc);
        }
      }
      // this table's own rows (also inside a row-level content control), not the rows of a
      // table nested in one of its cells: those are part of that cell, and listing them here
      // too wrote the nested table twice (once inside its cell, once as rows of this table)
      const rows = Array.from(child.getElementsByTagNameNS(W_NS, 'tr')).filter(tr => {
        for (let a = tr.parentNode as Element | null; a && a !== child; a = a.parentNode as Element | null) {
          if (a.nodeType === 1 && a.namespaceURI === W_NS && a.localName === 'tbl') return false;
        }
        return true;
      });
      for (let ri = 0; ri < rows.length; ri++) {
        const row = rows[ri];
        const rowText = extractTableRowText(row);
        const cellData = parseTableRowCells(row);
        paragraphs.push({
          text: rowText,
          pPrXml: '',
          richTokens: tokenizeToRich(rowText, ''),
          rawXml: serializeNode(row),
          inTable: true,
          isTableRow: true,
          tablePropsXml: tablePropsXml,
          cellData: cellData
        });
      }
    } else if (localName === 'sectPr') {
      // Skip — handled separately
    } else {
      // Passthrough block (content control / TOC sdt, custom XML, etc.). Give it its
      // real text so the diff can align it: with text '' every such block "matched"
      // an empty paragraph as equal, and one removed in the revision was silently
      // dropped instead of shown as deleted. The block is still emitted verbatim when
      // equal, and as a whole-block delete/insert when it changed.
      const blockText = Array.from(child.getElementsByTagNameNS(W_NS, 't'))
        .map(t => t.textContent || '').join(' ').replace(/\s+/g, ' ').trim();
      paragraphs.push({
        text: blockText,
        pPrXml: '',
        richTokens: tokenizeToRich(blockText, ''),
        rawXml: serializeNode(child),
        inTable: false,
        isPassthrough: true
      });
    }
  }

  return paragraphs;
}

function extractTableRowText(trElem: Element): string {
  let text = '';
  // the row's own cells (a nested table's cells are inside one of them: counted once, there)
  const cells = Array.from(trElem.childNodes).filter(n => (n as Element).nodeType === 1 && (n as Element).namespaceURI === W_NS && (n as Element).localName === 'tc') as Element[];
  for (let ci = 0; ci < cells.length; ci++) {
    const runs = cells[ci].getElementsByTagNameNS(W_NS, 'r');
    for (let ri = 0; ri < runs.length; ri++) {
      const run = runs[ri];
      // Same as the paragraph parser: moveFrom text is not part of the accepted view.
      let underMoveFrom = false;
      for (let anc = run.parentNode; anc && anc !== cells[ci]; anc = anc.parentNode) {
        const ae = anc as Element;
        if (ae.nodeType === 1 && ae.localName === 'moveFrom' && ae.namespaceURI === W_NS) { underMoveFrom = true; break; }
      }
      if (underMoveFrom) continue;
      for (let j = 0; j < run.childNodes.length; j++) {
        const c = run.childNodes[j] as Element;
        if (c.nodeType !== 1) continue;
        if (c.localName === 't') text += c.textContent;
        else if (c.localName === 'tab') text += '\t';
        else if (c.localName === 'br') text += '\n';
      }
      text += ' ';
    }
    if (ci < cells.length - 1) text += ' | ';
  }
  return text;
}

// cell children that are not content: anything else (w:tbl, w:sdt, w:customXml, …) holds text
// the cell's paragraphs do not
const CELL_MARKERS = /^(tcPr|p|bookmarkStart|bookmarkEnd|commentRangeStart|commentRangeEnd|proofErr|permStart|permEnd|moveFromRangeStart|moveFromRangeEnd|moveToRangeStart|moveToRangeEnd)$/;
function parseTableRowCells(trElem: Element): CellData {
  let trPrXml = '';
  const cells: CellInfo[] = [];
  let opaque = false;
  for (const child of Array.from(trElem.childNodes)) {
    if ((child as Element).nodeType !== 1) continue;
    const el = child as Element;
    if (el.localName === 'trPr') {
      trPrXml = serializeNode(el);
    } else if (el.localName === 'tc') {
      cells.push(parseOneCell(el));
      for (const k of Array.from(el.childNodes)) {
        if ((k as Element).nodeType === 1 && !CELL_MARKERS.test((k as Element).localName)) opaque = true;
      }
    }
  }
  return opaque ? { trPrXml, cells, opaque } : { trPrXml, cells };
}

function parseOneCell(tcElem: Element): CellInfo {
  let tcPrXml = '';
  const paragraphs: RichParagraph[] = [];
  const kids = Array.from(tcElem.childNodes).filter(n => n.nodeType === 1) as Element[];
  for (let i = 0; i < kids.length; i++) {
    const el = kids[i];
    if (el.localName === 'tcPr') {
      tcPrXml = serializeNode(el);
    } else if (el.localName === 'p') {
      const run = joinRun(kids, i);
      paragraphs.push(parseOneParagraph(run.el));
      i = run.last;
    }
  }
  // A cell that also holds other blocks (a nested table, a content control around paragraphs):
  // its text is all of it, in order, so the row compares and counts by what it really says.
  // (Such a row is shown as a whole-row replacement: CellData.opaque.)
  const opaque = kids.some(k => !CELL_MARKERS.test(k.localName));
  const text = opaque
    ? Array.from(tcElem.getElementsByTagNameNS(W_NS, 'p')).map(p => Array.from(p.getElementsByTagNameNS(W_NS, 't')).filter(t => {
        for (let a = t.parentNode as Element | null; a && a !== p; a = a.parentNode as Element | null) {
          if (a.nodeType === 1 && a.namespaceURI === W_NS && (a.localName === 'moveFrom' || a.localName === 'del' || a.localName === 'p')) return false;
        }
        return true;
      }).map(t => t.textContent || '').join('')).join(' ').replace(/\s+/g, ' ').trim()
    : paragraphs.map(p => p.text).join(' ');
  const richTokens: RichToken[] = [];
  for (const p of paragraphs) {
    richTokens.push(...p.richTokens);
  }
  return {
    tcPrXml,
    paragraphs,
    rawXml: serializeNode(tcElem),
    text,
    richTokens
  };
}

export function tokenizeToRich(text: string, rPrXml: string): RichToken[] {
  const tokens: RichToken[] = [];
  const regex = /\S+/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    tokens.push({ word: match[0], rPrXml: rPrXml || '' });
  }
  return tokens;
}

/** Look up the rPrXml for a character position in a run-based interval list.
 *  Runs are sorted by start, so a simple linear scan (most paragraphs have <10 runs) is fastest. */
function rPrAtPosition(runs: { start: number; end: number; rPrXml: string }[], pos: number): string {
  for (let i = runs.length - 1; i >= 0; i--) {
    if (pos >= runs[i].start) return runs[i].rPrXml;
  }
  return '';
}

// Dropped from the text: invisible characters that only affect line breaking (zero-width space,
// word joiner, soft hyphen, BOM, combining grapheme joiner). Kept: the joiners and direction
// marks that change how text RENDERS (ZWJ U+200D in emoji sequences, ZWNJ U+200C in Persian
// and Indic words, LRM/RLM U+200E/F and ALM U+061C in right-to-left text): an edited paragraph
// is rebuilt from this text, and without them "\uD83D\uDC68\u200D\u2696\uFE0F" became "\uD83D\uDC68\u2696\uFE0F" and mixed-direction text could
// reorder. Comparison ignores all of them either way (normalizeText).
const _invisibles = new Set([
  '\u200B', '\uFEFF', '\u2060', '\u00AD', '\u034F',
]);

// ---- paragraph joins a draft tracked ----
// Deleting the break between two paragraphs, with Track Changes on, marks the first one's
// paragraph mark as deleted (w:pPr/w:rPr/w:del; w:moveFrom when the text was moved away).
// The comparison reads each draft with its own tracked changes accepted, and accepting that
// mark joins the two paragraphs, as Word's Accept All does: the text runs on, and the
// properties of the paragraph after it survive. Without this, a draft's tracked join was read
// as two paragraphs, so a join both drafts had (or one only the original had) was lost.
const BETWEEN_PARAGRAPHS = /^(bookmarkStart|bookmarkEnd|commentRangeStart|commentRangeEnd|permStart|permEnd|proofErr|moveFromRangeStart|moveFromRangeEnd|moveToRangeStart|moveToRangeEnd)$/;
function wChild(el: Element, name: string): Element | null {
  for (const c of Array.from(el.childNodes)) if ((c as Element).nodeType === 1 && (c as Element).namespaceURI === W_NS && (c as Element).localName === name) return c as Element;
  return null;
}
/** The paragraph's mark was deleted or moved away (tracked), so it joins the next paragraph. */
function markRemoved(p: Element): boolean {
  const pPr = wChild(p, 'pPr');
  if (!pPr || wChild(pPr, 'sectPr')) return false;   // a section break is never joined away
  const rPr = wChild(pPr, 'rPr');
  return !!rPr && (!!wChild(rPr, 'del') || !!wChild(rPr, 'moveFrom'));
}
/**
 * From kids[i] (a w:p), the paragraphs that read as one: while a paragraph's mark is removed and
 * the next block (past bookmark and comment markers) is a paragraph, they join. Returns the
 * element to parse (kids[i] itself, or a detached joined copy) and the index of the last
 * paragraph used.
 */
function joinRun(kids: Element[], i: number): { el: Element; last: number } {
  const group: Element[] = [kids[i]];
  let last = i;
  while (markRemoved(kids[last])) {
    let j = last + 1;
    while (j < kids.length && kids[j].namespaceURI === W_NS && BETWEEN_PARAGRAPHS.test(kids[j].localName)) j++;
    if (j >= kids.length || kids[j].localName !== 'p' || kids[j].namespaceURI !== W_NS) break;
    group.push(kids[j]);
    last = j;
  }
  if (group.length === 1) return { el: kids[i], last: i };
  // the last paragraph's mark survives (its pPr); the earlier ones' content runs on into it
  const joined = group[group.length - 1].cloneNode(true) as Element;
  let at: Node | null = joined.firstChild;
  while (at && (at as Element).nodeType === 1 && (at as Element).localName === 'pPr') at = at.nextSibling;
  for (const p of group.slice(0, -1)) {
    for (const c of Array.from(p.childNodes)) {
      if ((c as Element).nodeType === 1 && (c as Element).localName === 'pPr') continue;
      joined.insertBefore(c.cloneNode(true), at);
    }
  }
  return { el: joined, last };
}

// ---- what a paragraph holds besides its words (InlineEvent) ----
const M_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/math';
// markers that are the revised draft's alone in a rebuilt paragraph (Word does not track them)
const INLINE_MARKS = /^(bookmarkStart|bookmarkEnd|commentRangeStart|commentRangeEnd|permStart|permEnd)$/;
// a draft's own spelling marks and revision ranges: not carried into a redline
const INLINE_DROPPED = /^(proofErr|moveFromRangeStart|moveFromRangeEnd|moveToRangeStart|moveToRangeEnd|customXml(?:Ins|Del|MoveFrom|MoveTo)Range(?:Start|End))$/;

/** A run's child as written back: its own namespace declarations kept (a picture's mc:, wp:, a:
 *  may be declared there), less the w: one every part declares at its root. */
const W_DECL = new RegExp(`^(<[^>]*?)\\s+xmlns:w="${W_NS.replace(/[.\/]/g, '\\$&')}"`);
function runChildXml(el: Element): string {
  return serializeNode(el).replace(W_DECL, '$1');
}

/** The opening tag of an element that wraps words (a hyperlink, a simple field). */
function openTag(el: Element): string {
  return serializeClean(el.cloneNode(false)).replace(/\s*\/>$/, '>');
}

/**
 * What a run's non-text content is, for finding the same one in the other draft (InlineEvent.key);
 * null for a comment mark, which is the revised draft's alone.
 */
function runKey(els: Element[]): string | null {
  const parts: string[] = [];
  for (const el of els) {
    const ln = el.localName;
    if (ln === 'commentReference' || ln === 'annotationRef') return null;
    if (ln === 'drawing' || ln === 'pict' || ln === 'object' || ln === 'AlternateContent') parts.push('picture');
    else if (ln === 'sym') parts.push(`sym:${el.getAttribute('w:font') || ''}:${(el.getAttribute('w:char') || '').toUpperCase()}`);
    else if (ln === 'fldChar') {
      // a form field's settings (a check box ticked or not) are part of what it is
      const ff = Array.from(el.childNodes).find(n => (n as Element).nodeType === 1 && (n as Element).localName === 'ffData');
      parts.push(`fld:${el.getAttribute('w:fldCharType') || ''}${ff ? ':' + serializeClean(ff) : ''}`);
    }
    else if (ln === 'instrText') parts.push('instr:' + (el.textContent || '').replace(/\s+/g, ' ').trim());
    else parts.push(ln);
  }
  return parts.join('|');
}

/**
 * Where an offset in a paragraph's clean text sits among its words (InlineEvent.w / .c): inside
 * or right after a word (no whitespace between); at the start of the word after the whitespace
 * it is in; before the first word's whitespace when it starts the paragraph; after everything
 * when it is in the whitespace after the last word.
 */
function inlinePlace(at: number, starts: number[], ends: number[]): { w: number; c: number } {
  const n = starts.length;
  let lo = 0, hi = n;   // the first word that ends at or after `at`
  while (lo < hi) { const mid = (lo + hi) >> 1; if (ends[mid] < at) lo = mid + 1; else hi = mid; }
  if (lo === n) return { w: n, c: 0 };
  if (at >= starts[lo]) return { w: lo, c: at - starts[lo] };
  return { w: lo, c: lo === 0 && at === 0 ? -1 : 0 };
}

/** One paragraph's XML from outside the body (a footnote's, a header's), read like a body
 *  paragraph: its words, their per-run formatting and the whitespace between them. */
export function parseParagraphXml(xml: string): RichParagraph {
  return parseOneParagraph(parseEl(xml));
}

function parseOneParagraph(pElem: Element): RichParagraph {
  let pPrXml = '';
  for (const c of Array.from(pElem.childNodes)) {
    if ((c as Element).nodeType === 1 && (c as Element).localName === 'pPr') {
      pPrXml = serializeNode(c);
      break;
    }
  }

  // Run-based intervals: each entry marks a character range sharing the same rPrXml.
  // For a paragraph with N characters across R runs, this stores R entries instead of N.
  const formatRuns: { start: number; end: number; rPrXml: string }[] = [];
  const textParts: string[] = [];
  let totalLen = 0;
  // what is not a word (see InlineEvent), at its offset in the raw text
  const events: { at: number; kind: InlineEvent['kind']; xml: string; key?: string }[] = [];
  const event = (kind: InlineEvent['kind'], xml: string, key?: string) => events.push({ at: totalLen, kind, xml, ...(key ? { key } : {}) });
  const addText = (s: string, rPrXml: string) => {
    if (!s) return;
    formatRuns.push({ start: totalLen, end: totalLen + s.length, rPrXml });
    textParts.push(s);
    totalLen += s.length;
  };

  // One run: its text, and what else it holds (a footnote mark, a picture, a field code …) as
  // runs of their own where they sit.
  const readRun = (run: Element) => {
    let rPrXml = '', ownRPr = '';
    for (const c of Array.from(run.childNodes)) {
      if ((c as Element).nodeType === 1 && (c as Element).localName === 'rPr') { rPrXml = serializeNode(c); ownRPr = serializeClean(c); break; }
    }
    let other: Element[] = [];
    const flush = () => {
      if (!other.length) return;
      const key = runKey(other);
      event('run', `<w:r>${ownRPr}${other.map(runChildXml).join('')}</w:r>`, key || undefined);
      other = [];
    };
    for (const c of Array.from(run.childNodes)) {
      const el = c as Element;
      if (el.nodeType !== 1) continue;
      const ln = el.localName, w = el.namespaceURI === W_NS;
      if (w && (ln === 'rPr' || ln === 'lastRenderedPageBreak' || ln === 'delText' || ln === 'delInstrText')) continue;
      if (w && ln === 't') { flush(); addText(c.textContent || '', rPrXml); }
      else if (w && ln === 'tab') { flush(); addText('\t', rPrXml); }
      else if (w && ln === 'br') {
        // a page or column break is kept apart from a line break in the whitespace (gaps)
        const ty = el.getAttribute('w:type');
        flush(); addText(ty === 'page' ? '\f' : ty === 'column' ? '\v' : '\n', rPrXml);
      }
      else other.push(el);
    }
    flush();
  };

  // The paragraph's content in order. Not read: runs nested inside another run's content (a
  // text box in a drawing group: its text is not the paragraph's, and the drawing is kept whole
  // as the run that holds it); <w:moveFrom> content (the source side of a Word move, absent from
  // the accepted view we compare: it lives at its moveTo destination); <w:del> content (deleted
  // in the draft; a deleted tab or break is a plain <w:tab/> or <w:br/>, which counted as
  // whitespace the accepted draft does not have).
  const walk = (el: Element) => {
    for (const c of Array.from(el.childNodes)) {
      const k = c as Element;
      if (k.nodeType !== 1) continue;
      const ln = k.localName;
      if (k.namespaceURI === M_NS && (ln === 'oMath' || ln === 'oMathPara')) { event('run', serializeNode(k), 'math'); continue; }
      if (k.namespaceURI !== W_NS) { walk(k); continue; }   // (as before, the runs inside are read)
      if (ln === 'pPr' || ln === 'del' || ln === 'moveFrom') continue;
      if (ln === 'r') { readRun(k); continue; }
      if (INLINE_MARKS.test(ln)) { event('mark', serializeClean(k)); continue; }
      if (INLINE_DROPPED.test(ln)) continue;
      if (ln === 'hyperlink' || ln === 'fldSimple') {
        event('open', openTag(k));
        walk(k);
        event('close', `</w:${ln}>`);
        continue;
      }
      if (ln === 'sdt') {
        let pr = '', content: Element | null = null;
        for (const s of Array.from(k.childNodes)) {
          const se = s as Element;
          if (se.nodeType !== 1) continue;
          if (se.localName === 'sdtContent') content = se;
          else if (se.localName === 'sdtPr' || se.localName === 'sdtEndPr') pr += serializeClean(se);
        }
        event('open', `<w:sdt>${pr}<w:sdtContent>`);
        if (content) walk(content);
        event('close', '</w:sdtContent></w:sdt>');
        continue;
      }
      walk(k);   // w:ins, w:moveTo, w:smartTag, w:customXml, w:dir, w:bdo …: their content is the paragraph's
    }
  };
  walk(pElem);

  const fullTextRaw = textParts.join('');

  // Clean invisible characters and normalize NBSP, rebuilding run intervals
  // without per-character arrays. We track a position mapping as we go.
  const cleanParts: string[] = [];
  const rawParts: string[] = [];   // the same characters, no-break spaces kept (for `gaps`)
  const cleanRuns: { start: number; end: number; rPrXml: string }[] = [];
  let cleanLen = 0;
  let runIdx = 0;
  let currentRunStart = 0;
  let currentRunRPr = formatRuns.length > 0 ? formatRuns[0].rPrXml : '';

  for (let i = 0; i < fullTextRaw.length; i++) {
    const ch = fullTextRaw[i];
    if (_invisibles.has(ch)) continue;

    // Find which format run this character belongs to
    while (runIdx < formatRuns.length - 1 && i >= formatRuns[runIdx].end) {
      runIdx++;
    }
    const rPr = runIdx < formatRuns.length ? formatRuns[runIdx].rPrXml : '';

    // If formatting changed, close previous run and start new one
    if (cleanLen > 0 && rPr !== currentRunRPr) {
      cleanRuns.push({ start: currentRunStart, end: cleanLen, rPrXml: currentRunRPr });
      currentRunStart = cleanLen;
      currentRunRPr = rPr;
    } else if (cleanLen === 0) {
      currentRunRPr = rPr;
    }

    // Normalize non-breaking space to regular space so tokenizer splits correctly (and a page or
    // column break to a line break: the gaps keep which it was)
    cleanParts.push(ch === '\u00A0' ? ' ' : (ch === '\f' || ch === '\v') ? '\n' : ch);
    rawParts.push(ch);
    cleanLen++;
  }
  // Close the final run
  if (cleanLen > currentRunStart) {
    cleanRuns.push({ start: currentRunStart, end: cleanLen, rPrXml: currentRunRPr });
  }

  const fullText = cleanParts.join('');

  const rawText = rawParts.join('');
  const richTokens: RichToken[] = [];
  const gaps: string[] = [];
  const starts: number[] = [], ends: number[] = [];
  let gapStart = 0;
  const regex = /\S+/g;
  let match;
  while ((match = regex.exec(fullText)) !== null) {
    const word = match[0];
    gaps.push(rawText.slice(gapStart, match.index));
    gapStart = match.index + word.length;
    starts.push(match.index); ends.push(gapStart);
    // Use the rPrXml from the first letter/digit character in the word,
    // not the first character overall. This avoids punctuation like quotes
    // or parens (often in a different run) from overriding the word's formatting.
    let rPr = rPrAtPosition(cleanRuns, match.index);
    for (let ci = 0; ci < word.length; ci++) {
      if (/[\p{L}\p{N}]/u.test(word[ci])) {
        rPr = rPrAtPosition(cleanRuns, match.index + ci);
        break;
      }
    }
    // If the word spans more than one source format run (e.g. bold term + non-bold
    // `")`), record the per-run segments so emission can keep each piece's own
    // formatting instead of forcing the whole word into the first-letter rPr.
    const segments: { text: string; rPrXml: string }[] = [];
    let segStart = 0;
    let segRPr = rPrAtPosition(cleanRuns, match.index);
    for (let ci = 1; ci < word.length; ci++) {
      const r = rPrAtPosition(cleanRuns, match.index + ci);
      if (r !== segRPr) {
        segments.push({ text: word.slice(segStart, ci), rPrXml: segRPr });
        segStart = ci;
        segRPr = r;
      }
    }
    segments.push({ text: word.slice(segStart), rPrXml: segRPr });
    richTokens.push({
      word: word,
      rPrXml: rPr,
      ...(segments.length > 1 ? { segments } : {}),
    });
  }

  gaps.push(rawText.slice(gapStart));

  // each event's place among the words: its offset in the clean text (invisible characters
  // removed), then the word it is in or beside (InlineEvent.w / .c)
  const dropped: number[] = [0];   // dropped[k]: invisible characters among the first k raw ones
  for (let i = 0; i < fullTextRaw.length; i++) dropped.push(dropped[i] + (_invisibles.has(fullTextRaw[i]) ? 1 : 0));
  const inline: InlineEvent[] = events.map(ev => {
    const at = ev.at - dropped[ev.at];
    return { ...inlinePlace(at, starts, ends), kind: ev.kind, xml: ev.xml, ...(ev.key ? { key: ev.key } : {}) };
  });

  return {
    text: fullText,
    pPrXml,
    richTokens,
    gaps,
    inline,
    rawXml: serializeNode(pElem),
    inTable: false
  };
}
